"use client";

import { useLayoutEffect } from "react";

/**
 * Browsers restore scroll on reload unless history.scrollRestoration is
 * "manual" (set early from layout via beforeInteractive script). This keeps
 * the document at the top after hydration and after late layout (fonts, images)
 * in case anything nudges scroll. When there is a hash, deep links still work.
 */
export function ScrollMountFix() {
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) return;

    let userInteracted = false;
    const stopRestoring = () => { userInteracted = true; };
    const toTop = () => { if (!userInteracted) window.scrollTo(0, 0); };
    // Late font/image loading must never pull a visitor back after they start scrolling.
    const inputs = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
    inputs.forEach((type) => window.addEventListener(type, stopRestoring, { passive: true }));

    toTop();

    const onLoad = () => toTop();
    window.addEventListener("load", onLoad);

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(toTop);
    });

    const t = window.setTimeout(toTop, 0);

    return () => {
      window.removeEventListener("load", onLoad);
      window.clearTimeout(t);
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      inputs.forEach((type) => window.removeEventListener(type, stopRestoring));
    };
  }, []);

  return null;
}
